import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { tr } from '../lib/i18n';

/**
 * Une photo qui s'agrandit en plein écran, avec un bouton pour fermer.
 *
 * <p>Les photos s'ouvraient dans une nouvelle page : dans SwissTech installé
 * sur l'iPhone, cette page n'avait ni barre ni bouton retour, et l'on ne
 * pouvait plus revenir à l'application. La photo s'affiche maintenant
 * par-dessus l'écran, et « ✕ Fermer » (ou un toucher à côté) y ramène.
 *
 * @param {object} props propriétés
 * @param {string} props.src adresse de l'image
 * @param {string} [props.alt] texte de remplacement et légende
 * @param {object} [props.style] style de la vignette
 * @param {import('react').ReactNode} [props.children] vignette à la place de l'image
 * @returns {import('react').ReactElement} la vignette
 */
export function PhotoAgrandie({ src, alt = '', style, children }) {
  const [ouverte, setOuverte] = useState(false);
  useEffect(() => {
    if (!ouverte) return undefined;
    const touche = (e) => e.key === 'Escape' && setOuverte(false);
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [ouverte]);
  return (
    <>
      <button
        type="button"
        aria-label={tr('Agrandir la photo')}
        onClick={(e) => {
          e.stopPropagation();
          setOuverte(true);
        }}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          padding: 0,
          border: 'none',
          background: 'none',
          cursor: 'zoom-in',
        }}
      >
        {children || (
          <img
            src={src}
            alt={alt}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', ...style }}
          />
        )}
      </button>
      {ouverte &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={alt || tr('Photo')}
            onClick={() => setOuverte(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              background: 'rgba(0,0,0,.92)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'max(56px, var(--st-haut)) 10px max(20px, env(safe-area-inset-bottom))',
              boxSizing: 'border-box',
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOuverte(false);
              }}
              style={{
                position: 'absolute',
                top: 'max(12px, var(--st-haut))',
                right: 12,
                background: '#fff',
                color: '#0F172A',
                border: 'none',
                borderRadius: 99,
                padding: '10px 16px',
                fontSize: 15,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              {tr('✕ Fermer')}
            </button>
            <img
              src={src}
              alt={alt}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
